/**
 * `/audit` — Audit & incidents (§16.2): append-only actor/action/before-after evidence (JsonDiff), filters,
 * support-case linkage, incident severity list, access grants, and the watermarked audit export (§16.3).
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { AuditActorKind, type AuditEntryDto } from '@windrop/contracts';
import { Button, useIntl, useToast } from '@windrop/ui';
import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { useSession } from '../../auth/session';
import { DataTable } from '../../components/DataTable';
import { EmptyState, ErrorState, NoDataYet } from '../../components/EmptyState';
import { JsonDiff } from '../../components/JsonDiff';
import { PageHeader } from '../../components/PageHeader';
import { ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { Timeline, type TimelineEvent } from '../../components/Timeline';
import { auditConsoleApi } from '../../lib/api/analytics';
import { isApiError } from '../../lib/api/client';

export const AUDIT_TABS = [
  { id: '', label: 'Events' },
  { id: 'incidents', label: 'Incidents' },
  { id: 'access-grants', label: 'Access grants' },
] as const;

export function AuditPage() {
  return (
    <>
      <PageHeader
        title="Audit & incidents"
        description="Hash-chained, append-only evidence of every actor and action with before/after state. Nothing here can be edited or deleted."
      />
      <ul className="wdc-tabs" role="list">
        {AUDIT_TABS.map((t) => (
          <li key={t.id}>
            <NavLink to={`/audit${t.id ? `/${t.id}` : ''}`} end>
              {t.label}
            </NavLink>
          </li>
        ))}
      </ul>
      <Outlet />
    </>
  );
}

/** Case / object links an audit entry carries (support-case linkage, §16.2). */
export function entryLinks(e: AuditEntryDto): { label: string; to: string }[] {
  const links: { label: string; to: string }[] = [];
  const d = e.details as Record<string, unknown>;
  const caseRef =
    typeof d.caseId === 'string' ? d.caseId : typeof d.supportCaseId === 'string' ? d.supportCaseId : null;
  if (e.objectType === 'support_case')
    links.push({ label: `Case ${e.objectId}`, to: `/claims?case=${e.objectId}` });
  else if (caseRef) links.push({ label: `Case ${caseRef}`, to: `/claims?case=${caseRef}` });
  if (e.objectType === 'lottery') links.push({ label: 'Lottery', to: `/lotteries/${e.objectId}/audit` });
  if (e.objectType === 'participant')
    links.push({ label: 'Participant', to: `/audience/participants/${e.objectId}` });
  if (e.objectType === 'export') links.push({ label: 'Export', to: '/audience/exports' });
  if (e.objectType === 'claim') links.push({ label: 'Claim', to: `/claims/${e.objectId}` });
  if (e.objectType === 'win' || e.objectType === 'winner_contact')
    links.push({ label: 'Winner', to: `/winners` });
  return links;
}

export function AuditEventsTab() {
  const toast = useToast();
  const [filters, setFilters] = useState({
    action: '',
    objectType: '',
    objectId: '',
    actorKind: '',
    actorId: '',
    from: '',
    to: '',
  });
  const active = Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) as Record<string, string>;
  const query = useQuery({
    queryKey: ['audit', active],
    queryFn: () => auditConsoleApi.list({ ...active, limit: 100 } as never),
  });
  const exportJob = useMutation({
    mutationFn: () => auditConsoleApi.export(active as never),
    onSuccess: (job) =>
      toast.toast({
        title: 'Audit export queued',
        description: `Watermark ${job.watermark} · find it under Audience › Exports.`,
        tone: 'success',
      }),
  });
  const set = (k: keyof typeof filters) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFilters({ ...filters, [k]: e.target.value });
  const events: TimelineEvent[] = (query.data?.items ?? []).map((e) => {
    const details = e.details as { before?: unknown; after?: unknown; purpose?: unknown };
    const links = entryLinks(e);
    return {
      id: e.auditId,
      at: e.at,
      title: humanizeStatus(e.action.replace(/[.-]/g, '_')),
      actor: `${e.actorKind.toLowerCase()} ${e.actorId || 'system'}${e.objectType ? ` · ${e.objectType} ${e.objectId}` : ''}`,
      tone:
        e.action.includes('erase') || e.action.includes('cancel') || e.action.includes('reject')
          ? 'warning'
          : undefined,
      detail: (
        <div style={{ display: 'grid', gap: 'var(--wd-space-2)' }}>
          {details.before !== undefined || details.after !== undefined ? (
            <JsonDiff before={details.before} after={details.after} />
          ) : null}
          {e.purpose ? (
            <p>
              <strong>Purpose:</strong> {e.purpose}
            </p>
          ) : null}
          {links.length ? (
            <p className="wd-btn-group">
              {links.map((l) => (
                <Link key={l.to} className="wd-btn" data-variant="quiet" to={l.to}>
                  {l.label}
                </Link>
              ))}
            </p>
          ) : null}
          <details>
            <summary>Evidence hashes</summary>
            <code style={{ display: 'block', fontSize: 12 }}>hash {e.hash}</code>
            <code style={{ display: 'block', fontSize: 12 }}>prev {e.prevHash || '—'}</code>
          </details>
        </div>
      ),
    };
  });
  return (
    <>
      <form
        className="wdc-form-grid"
        style={{ marginBottom: 'var(--wd-space-4)' }}
        onSubmit={(e) => e.preventDefault()}
        aria-label="Audit filters"
      >
        <label className="wd-field">
          <span className="wd-field__label">Action prefix</span>
          <input
            className="wd-input"
            value={filters.action}
            onChange={set('action')}
            placeholder="lottery.publish"
            data-testid="audit-action"
          />
        </label>
        <label className="wd-field">
          <span className="wd-field__label">Object type</span>
          <input
            className="wd-input"
            value={filters.objectType}
            onChange={set('objectType')}
            placeholder="lottery, claim, export…"
          />
        </label>
        <label className="wd-field">
          <span className="wd-field__label">Object id</span>
          <input className="wd-input" value={filters.objectId} onChange={set('objectId')} />
        </label>
        <label className="wd-field">
          <span className="wd-field__label">Actor kind</span>
          <select className="wd-select" value={filters.actorKind} onChange={set('actorKind')}>
            <option value="">Any</option>
            {AuditActorKind.options.map((k) => (
              <option key={k} value={k}>
                {humanizeStatus(k)}
              </option>
            ))}
          </select>
        </label>
        <label className="wd-field">
          <span className="wd-field__label">Actor id</span>
          <input className="wd-input" value={filters.actorId} onChange={set('actorId')} />
        </label>
        <label className="wd-field">
          <span className="wd-field__label">From</span>
          <input
            className="wd-input"
            type="datetime-local"
            value={filters.from}
            onChange={(e) =>
              setFilters({ ...filters, from: e.target.value ? new Date(e.target.value).toISOString() : '' })
            }
          />
        </label>
        <label className="wd-field">
          <span className="wd-field__label">To</span>
          <input
            className="wd-input"
            type="datetime-local"
            value={filters.to}
            onChange={(e) =>
              setFilters({ ...filters, to: e.target.value ? new Date(e.target.value).toISOString() : '' })
            }
          />
        </label>
        <div className="wd-btn-group" style={{ alignSelf: 'end' }}>
          <Button variant="secondary" onClick={() => exportJob.mutate()} loading={exportJob.isPending}>
            Export (watermarked)
          </Button>
        </div>
      </form>
      {query.isError ? (
        <ErrorState
          title="Audit log unavailable"
          body="Retry, or check the API status."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : query.isPending ? (
        <NoDataYet what="audit entries" hint="Loading…" />
      ) : (
        <Timeline events={events} emptyText="No audit entries match these filters." />
      )}
    </>
  );
}

export function AuditIncidentsTab() {
  const intl = useIntl();
  const query = useQuery({
    queryKey: ['audit', 'incidents'],
    queryFn: auditConsoleApi.incidents,
    refetchInterval: 30_000,
  });
  const rows = query.data?.items ?? [];
  return (
    <>
      <p className="wd-muted">
        Severity 1 (§20.2): active outcome corruption, duplicate charging or allocation, cross-tenant exposure
        or a draw at risk — freeze affected writes, preserve evidence, name an incident commander.
      </p>
      <DataTable
        caption="Open incidents by severity"
        rows={rows}
        rowKey={(r) => r.incidentId}
        loading={query.isPending}
        pageSize={0}
        emptyState={
          <EmptyState
            title="No open incidents"
            body="Escalations, reconciliation exceptions, failed draw checks and fulfillment exceptions appear here."
            action={<Button href="/">Open dashboard</Button>}
          />
        }
        columns={[
          {
            id: 'severity',
            header: 'Severity',
            sortValue: (r) => r.severity,
            cell: (r) => (
              <ToneBadge tone={r.severity === 'SEV1' ? 'error' : r.severity === 'SEV2' ? 'warning' : 'info'}>
                {r.severity}
              </ToneBadge>
            ),
          },
          { id: 'kind', header: 'Kind', sortValue: (r) => r.kind, cell: (r) => humanizeStatus(r.kind) },
          { id: 'title', header: 'Incident', cell: (r) => r.title },
          { id: 'status', header: 'Status', cell: (r) => humanizeStatus(r.status) },
          { id: 'at', header: 'Since', sortValue: (r) => r.at, cell: (r) => intl.formatRelative(r.at) },
        ]}
        rowActions={(r) => (
          <Link
            className="wd-btn"
            data-variant="quiet"
            to={
              r.kind === 'reconciliation_exception'
                ? '/finance'
                : r.kind === 'draw_check_failure'
                  ? '/draws'
                  : r.kind === 'fulfillment_exception'
                    ? '/fulfillment'
                    : '/claims'
            }
          >
            Open
          </Link>
        )}
      />
    </>
  );
}

export function AuditAccessGrantsTab() {
  const intl = useIntl();
  const { session } = useSession();
  const superAdmin = session?.roles.includes('PLATFORM_SUPER_ADMIN') ?? false;
  const query = useQuery({
    queryKey: ['audit', 'access-grants'],
    queryFn: auditConsoleApi.accessGrants,
    enabled: superAdmin,
    retry: false,
  });
  if (!superAdmin)
    return (
      <EmptyState
        title="Access grants are listed for platform super-admins"
        body="Tenant staff see privileged access in the audit events (grantId on every enhanced-audit row)."
        action={<Button href="/audit">Open audit events</Button>}
      />
    );
  const rows = query.data ?? [];
  return (
    <DataTable
      caption="Just-in-time privileged access grants (§3)"
      rows={rows}
      rowKey={(g) => g.grantId ?? g.id ?? `${g.tenantId}-${g.createdAt}`}
      loading={query.isPending}
      pageSize={0}
      emptyState={
        <EmptyState
          title="No grants"
          body="JIT grants appear here with reason, ticket and expiry."
          action={<Button href="/settings">Open settings</Button>}
        />
      }
      columns={[
        { id: 'tenant', header: 'Tenant', cell: (g) => g.tenantSlug ?? g.tenantId ?? '—' },
        { id: 'reason', header: 'Reason', cell: (g) => g.reason ?? '—' },
        { id: 'ticket', header: 'Ticket', cell: (g) => g.ticketRef ?? '—' },
        {
          id: 'granted',
          header: 'Granted',
          cell: (g) =>
            (g.grantedAt ?? g.createdAt) ? intl.formatDateTime(String(g.grantedAt ?? g.createdAt)) : '—',
        },
        {
          id: 'expires',
          header: 'Expires',
          cell: (g) => (g.expiresAt ? intl.formatDateTime(g.expiresAt) : '—'),
        },
        {
          id: 'state',
          header: 'State',
          cell: (g) => (
            <ToneBadge tone={g.revokedAt ? 'neutral' : 'warning'}>
              {g.revokedAt ? 'Revoked' : 'Active'}
            </ToneBadge>
          ),
        },
      ]}
    />
  );
}
